import { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { changePassword } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Key } from "lucide-react";

const PasswordChange = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error("All fields are required");
      return;
    }

    if (formData.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    setLoading(true);
    try {
      await changePassword(formData.currentPassword, formData.newPassword, formData.confirmPassword);
      toast.success("Password changed successfully!");
      
      // Reset form
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Current password is incorrect");
      } else {
        toast.error(error.response?.data?.detail || "Failed to change password");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="password-change-page">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-8 h-8 text-[#5D4037]" />
            <h1 className="text-4xl font-bold text-[#5D4037]">Change Password</h1>
          </div>
          <p className="text-[#795548]">
            Update your admin password to keep your account secure.
          </p>
        </div>

        <div className="max-w-2xl">
          <div className="bg-white rounded-xl shadow-sm border border-[#D7CCC8]/50 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="currentPassword" className="text-sm font-semibold text-[#5D4037] mb-2 block">
                  Current Password
                </Label>
                <Input
                  id="currentPassword"
                  data-testid="current-password-input"
                  type="password"
                  value={formData.currentPassword}
                  onChange={(e) => handleChange("currentPassword", e.target.value)}
                  placeholder="Enter your current password"
                  className="text-lg"
                />
              </div>

              <div className="border-t border-[#D7CCC8] pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Key className="w-5 h-5 text-[#8A9A5B]" />
                  <h3 className="text-lg font-semibold text-[#5D4037]">New Password</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="newPassword" className="text-sm font-semibold text-[#5D4037] mb-2 block">
                      New Password
                    </Label>
                    <Input
                      id="newPassword"
                      data-testid="new-password-input"
                      type="password"
                      value={formData.newPassword}
                      onChange={(e) => handleChange("newPassword", e.target.value)}
                      placeholder="Enter new password (min 8 characters)"
                      className="text-lg"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword" className="text-sm font-semibold text-[#5D4037] mb-2 block">
                      Confirm New Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      data-testid="confirm-password-input"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange("confirmPassword", e.target.value)}
                      placeholder="Re-enter new password"
                      className="text-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-[#D7CCC8] pt-6">
                <div className="bg-[#FFF8E1] border border-[#FFD54F] rounded-lg p-4 mb-6">
                  <p className="text-sm text-[#795548]">
                    <strong>Password Requirements:</strong>
                  </p>
                  <ul className="text-xs text-[#795548] mt-2 space-y-1 list-disc list-inside">
                    <li>Minimum 8 characters long</li>
                    <li>Use a strong, unique password</li>
                    <li>Don't reuse passwords from other accounts</li>
                  </ul>
                </div>

                <Button
                  data-testid="change-password-button"
                  type="submit"
                  disabled={loading}
                  className="bg-[#5D4037] text-white hover:bg-[#4E342E] px-8"
                >
                  {loading ? "Changing Password..." : "Change Password"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PasswordChange;
